import React from 'react';
import { useCurrentUser } from '@docavenue/components';
import { CardHcd } from '@maiia/model/generated/model/api-patient/api-patient';
import { Separator } from '@kiiwi/ui';
import dynamic from 'next/dynamic';
import TLCSkeleton from './TLCSkeleton';
import { useTranslation } from '@/src/i18n';
import useTlcAmcProtectedPatients from '@/src/hooks/useTlcAmcProtectedPatients';
import TlcAmcCard from '@/components/organisms/TlcAmcCard/TlcAmcCard';
import { getPrimaryProtectedPatient } from '../TlcAmc/utils';
import UpcomingTLCs from './UpcomingTLCs';
import EmptyState from '@/components/organisms/SearchResults/EmptyState-v2';
import useTlcCards from '@/src/hooks/useTlcCards-v2';
import ImmediateTLCs from './ImmediateTLCs';

// ssr false to avoid hydration issue with the form field id generated
const TLCFilterBar = dynamic(
  () => import('@/components/molecules/FilterBar/TLCFilterBar'),
  { ssr: false },
);

type Props = {
  filters: Record<string, any>;
  onFilterChange: Function;
};

const TLC = ({ filters, onFilterChange }: Props) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  const {
    items: knownPractitioners,
    total: totalKnownPractitioners,
    queryResult: {
      isInitialLoading: isInitialLoadingKnownPractitioners,
      isSuccess: isSuccessKnownPractitioners,
      fetchNextPage: fetchNextKnownPractitionersPage,
      hasNextPage: hasNextKnownPractitionersPage,
    },
  } = useTlcCards();

  const {
    items: unknownPractitioners,
    total: totalUnknownPractitioners,
    queryResult: {
      isInitialLoading: isInitialLoadingUnknownPractitioners,
      isSuccess: isSuccessUnknownPractitioners,
      fetchNextPage: fetchNextUnknownPractitionersPage,
      hasNextPage: hasNextUnknownPractitionersPage,
    },
  } = useTlcCards(false);

  const { data: protectedPatients, isFetched } = useTlcAmcProtectedPatients();
  const isProtectedPatientsEmpty = isFetched && protectedPatients?.total === 0;
  const defaultPatient = currentUser?.userPatientInformation?.userPatientProfiles?.find(
    patient => patient.isDefault,
  );
  const protectedPatient = React.useMemo(
    () =>
      (protectedPatients?.items ?? []).length > 0 &&
      getPrimaryProtectedPatient(protectedPatients?.items, defaultPatient),
    [protectedPatients?.items, defaultPatient],
  );
  const shouldShowTlcAmcCard = protectedPatients && !isProtectedPatientsEmpty;
  const isTLCEncouraged = protectedPatient?.service?.encourageTlc;

  const [upcomingTlcCount, setUpcomingTlcCount] = React.useState(0);
  const handleUpcomingTlcItemsChange = (upcomingItems?: CardHcd[]) => {
    setUpcomingTlcCount(upcomingItems?.length || 0);
  };

  const noImmediateTLCs =
    knownPractitioners.length === 0 && unknownPractitioners.length === 0;
  const mustShowTLCSkeleton =
    (!currentUser?.id ? true : isInitialLoadingKnownPractitioners) &&
    isInitialLoadingUnknownPractitioners;

  return (
    <>
      <TLCFilterBar
        filters={filters}
        onChange={onFilterChange}
        numberOfSearchResults={unknownPractitioners.length + upcomingTlcCount}
      />
      <div className="flex flex-col items-center justify-center px-4 pt-[137px] sm:pt-0">
        <div className="mt-6 w-full max-w-[760px] sm:mt-8">
          {mustShowTLCSkeleton ? (
            <TLCSkeleton
              nbrSkeleton={
                unknownPractitioners.length ? unknownPractitioners.length : 4
              }
            />
          ) : (
            <>
              {shouldShowTlcAmcCard && isTLCEncouraged && (
                <>
                  <TlcAmcCard
                    protectedPatientItems={protectedPatients.items}
                    isEncouraged={isTLCEncouraged}
                  />
                  <Separator className="my-6 sm:my-8" />
                </>
              )}
              {!!currentUser?.id &&
                isSuccessKnownPractitioners &&
                knownPractitioners.length !== 0 && (
                  <>
                    <ImmediateTLCs
                      practitioners={knownPractitioners}
                      total={totalKnownPractitioners}
                      hasNextPage={hasNextKnownPractitionersPage as boolean}
                      fetchNextPage={
                        fetchNextKnownPractitionersPage as () => void
                      }
                      titleKey="tlc-known-practitioners-available-title"
                      subtitleKey="tlc-known-practitioners-available-subtitle"
                      mustHideSectorCard
                    />
                    {((isSuccessUnknownPractitioners &&
                      unknownPractitioners.length !== 0) ||
                      (shouldShowTlcAmcCard && !isTLCEncouraged) ||
                      upcomingTlcCount !== 0) && (
                      <Separator className="my-6 sm:my-8" />
                    )}
                  </>
                )}
              {isSuccessUnknownPractitioners &&
                unknownPractitioners.length !== 0 && (
                  <>
                    <ImmediateTLCs
                      practitioners={unknownPractitioners}
                      total={totalUnknownPractitioners}
                      hasNextPage={hasNextUnknownPractitionersPage as boolean}
                      fetchNextPage={
                        fetchNextUnknownPractitionersPage as () => void
                      }
                      titleKey={
                        shouldShowTlcAmcCard
                          ? 'tlc-all-available-practitioners-title-tlc-amc-card-visible'
                          : 'tlc-all-available-practitioners-title-tlc-amc-card-hidden'
                      }
                      subtitleKey="tlc-all-available-practitioners-subtitle"
                      indexItemForSectorCard={2}
                    />
                    {((shouldShowTlcAmcCard && !isTLCEncouraged) ||
                      upcomingTlcCount !== 0) && (
                      <Separator className="my-6 sm:my-8" />
                    )}
                  </>
                )}
              {noImmediateTLCs && (
                <div className="w-full">
                  <EmptyState
                    text={t('no_teleconsultation_available')}
                    subtext={t('come-back-later')}
                  />
                  {((shouldShowTlcAmcCard && !isTLCEncouraged) ||
                    upcomingTlcCount !== 0) && (
                    <Separator className="my-6 sm:my-8" />
                  )}
                </div>
              )}
              {shouldShowTlcAmcCard && !isTLCEncouraged && (
                <>
                  <TlcAmcCard
                    protectedPatientItems={protectedPatients.items}
                    isEncouraged={isTLCEncouraged}
                  />
                  {upcomingTlcCount !== 0 && (
                    <Separator className="my-6 sm:my-8" />
                  )}
                </>
              )}
              <UpcomingTLCs
                filters={filters}
                onItemsChange={handleUpcomingTlcItemsChange}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TLC;
